import { createId } from "@paralleldrive/cuid2"
import { sql } from "drizzle-orm"
import {
  boolean,
  doublePrecision,
  foreignKey,
  index,
  integer,
  jsonb,
  numeric,
  pgTable,
  pgEnum,
  serial,
  text,
  timestamp,
  uniqueIndex,
  varchar,
  type AnyPgColumn,
} from "drizzle-orm/pg-core"
import type { ProjectVersion } from "../types"
import { user } from "./auth-schema"
import { configurationVersions } from "./store-configuration-schema"

// =============================================================================
// Enums
// =============================================================================

export const fileTypeEnum = pgEnum("file_type", [
  "input_blueprint",
  "cropped_image",
  "cropped_image_hires",
  "output_report",
  "output_converter_pdf",
  "output_converter_xlsx",
  "reference_doc",
])

export const storageBackendEnum = pgEnum("storage_backend", [
  "filesystem",
  "vercel_blob",
  "azure_storage",
])

export const projectStatusEnum = pgEnum("project_status", ["draft", "approved", "archived"])

export const fixtureValidationStatusEnum = pgEnum("fixture_validation_status", ["pass", "fail"])

export const fixtureValidationTriggerSourceEnum = pgEnum("fixture_validation_trigger_source", [
  "crop_saved",
  "work_area_saved",
  "revalidate",
])

// =============================================================================
// Tables with no dependencies
// =============================================================================

export const auditEvents = pgTable(
  "audit_events",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    occurredAt: timestamp("occurred_at", { withTimezone: true }).defaultNow().notNull(),
    scopeId: text("scope_id"),
    actorId: text("actor_id").notNull(),
    actorType: text("actor_type").notNull(),
    actorDisplay: text("actor_display").notNull(),
    action: text().notNull(),
    entityType: text("entity_type").notNull(),
    entityId: text("entity_id").notNull(),
    entityName: text("entity_name"),
    entityPath: text("entity_path"),
    summary: text().notNull(),
    diff: jsonb().$type<Record<string, {}> | null>().default(null),
    metadata: jsonb().$type<Record<string, {}> | null>().default(null),
  },
  (table) =>
    [
      index("audit_events_entity_idx").on(table.entityType, table.entityId, table.occurredAt),
      index("audit_events_actor_idx").on(table.actorId, table.occurredAt),
      index("audit_events_scope_idx").on(table.scopeId, table.occurredAt),
    ] as const,
)

export const countries = pgTable(
  "countries",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    name: text().notNull(),
    code: text(),
  },
  (table) => [
    index("countries_name_trgm_idx").using("gin", table.name.op("gin_trgm_ops")),
    index("countries_name_idx").on(table.name),
    index("countries_code_idx").on(table.code),
  ],
)

export const category = pgTable(
  "category",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    name: text().notNull(),
    defaultLocation: text("default_location"),
    color: text(),
  },
  (table) => [
    uniqueIndex("category_name_idx").using("btree", table.name.asc().nullsLast().op("text_ops")),
  ],
)

// =============================================================================
// Tables depending on countries
// =============================================================================

export const stores = pgTable(
  "stores",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    // External system identifier (branch number from Stores API)
    branchNumber: text("branch_number"),
    name: text().notNull(),
    countryId: text("country_id").notNull(),
    // Display code of the branch-number region the planner picked for this store
    // (e.g. "BE-FL"), from country_code_mappings. Persisted so PDF export uses
    // the explicit choice instead of re-deriving it from the branch number.
    // Hard FK to the mapping's display_code (the region's natural key). Nullable
    // until every environment is backfilled; flipped to NOT NULL afterwards.
    regionCode: text("region_code").references((): AnyPgColumn => countryCodeMappings.displayCode, {
      onUpdate: "cascade",
      onDelete: "restrict",
    }),
    locale: text().notNull(),
    city: text().notNull(),
    streetName: text("street_name"),
    houseNumber: text("house_number"),
    houseNumberAddition: text("house_number_addition"),
    postalCode: text("postal_code"),
    province: text(),
    stateCode: text("state_code"),
    fullAddress: text("full_address").notNull(),
    rawStreet: text("raw_street"),
    rawPostal: text("raw_postal"),
    phoneNumber: text("phone_number"),
    latitude: doublePrecision(),
    longitude: doublePrecision(),
    siteUrl: text("site_url"),
    // Store format/concept version (e.g. "1.0", "2.0"). Allowed values are gated
    // by ALLOWED_STORE_VERSIONS in src/features/stores/storeVersions.ts. Null
    // means unclassified.
    version: text("version"),
    // Store lifecycle dates from external system
    initialOpeningDate: timestamp("initial_opening_date", { withTimezone: true }),
    permanentlyClosedDate: timestamp("permanently_closed_date", { withTimezone: true }),
    // Sync tracking
    externalDataTimestamp: timestamp("external_data_timestamp", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }),
  },
  (table) => [
    foreignKey({
      columns: [table.countryId],
      foreignColumns: [countries.id],
      name: "stores_country_id_countries_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    index("stores_country_id_idx").on(table.countryId),
    index("stores_name_trgm_idx").using("gin", table.name.op("gin_trgm_ops")),
    index("stores_city_trgm_idx").using("gin", table.city.op("gin_trgm_ops")),
    index("stores_name_idx").on(table.name),
    index("stores_city_idx").on(table.city),
    index("stores_locale_idx").on(table.locale),
    uniqueIndex("stores_branch_number_idx").on(table.branchNumber),
  ],
)

// =============================================================================
// Tables depending on category
// =============================================================================

export const merchandiseGroup = pgTable(
  "merchandise_group",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    merchandiseGroupName: text("merchandise_group_name").notNull(),
    categoryId: text("category_id").notNull(),
    abbreviation: text(),
    bmWall: numeric("bm_wall"),
    bmGondola: numeric("bm_gondola"),
    bmTotal: numeric("bm_total"),
    moveToGondolaPriority: integer("move_to_gondola_priority"),
    wallMin: integer("wall_min"),
    wallMax: integer("wall_max"),
    gondolaMin: integer("gondola_min"),
    gondolaMax: integer("gondola_max"),
    priority: integer(),
  },
  (table) => [
    uniqueIndex("merchandise_group_name_idx").using(
      "btree",
      table.merchandiseGroupName.asc().nullsLast().op("text_ops"),
    ),
    foreignKey({
      columns: [table.categoryId],
      foreignColumns: [category.id],
      name: "merchandise_group_category_id_category_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
  ],
)

// =============================================================================
// Tables depending on merchandiseGroup
// =============================================================================

export const merchandiseGroupConfiguration = pgTable(
  "merchandise_group_configuration",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    merchandiseGroupId: text("merchandise_group_id").notNull(),
    configuration: jsonb().notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    lastModified: timestamp("last_modified", {
      withTimezone: true,
    }).defaultNow(),
  },
  (table) => [
    uniqueIndex("merchandise_group_configuration_mg_id_unique").using(
      "btree",
      table.merchandiseGroupId.asc().nullsLast().op("text_ops"),
    ),
    foreignKey({
      columns: [table.merchandiseGroupId],
      foreignColumns: [merchandiseGroup.id],
      name: "merchandise_group_configuration_merchandise_group_id_merchandis",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
)

// =============================================================================
// Tables depending on stores
// =============================================================================

// Snapshot of a store's address taken when a project is created (BLU-589). The
// store's live address can change after a store relocates; the snapshot keeps
// the address as it was at creation time so existing projects are not rewritten.
export type StoreAddressSnapshot = {
  fullAddress: string
  city: string
  streetName: string | null
  houseNumber: string | null
  houseNumberAddition: string | null
  postalCode: string | null
  province: string | null
  stateCode: string | null
  countryId: string
  regionCode: string | null
  // Resolved country display code (e.g. "BE-FL"), matching getStoreConfiguration.
  countryCode: string | null
}

export const projectsTable = pgTable(
  "project",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    name: varchar({ length: 255 }).notNull(),
    version: serial().notNull(),
    country: varchar({ length: 100 }).notNull(),
    location: varchar({ length: 255 }).notNull(),
    date: timestamp().notNull(),
    lastUpdate: timestamp("last_update").defaultNow().notNull(),
    author: varchar({ length: 255 }).notNull(),
    metadata: jsonb("metadata").$type<ProjectVersion>().notNull().default({}),
    storeId: text("store_id"), // Link to stores table
    // Address of the store captured at project creation (BLU-589). Null for
    // projects created before this feature; reads fall back to the live store.
    storeAddressSnapshot: jsonb("store_address_snapshot").$type<StoreAddressSnapshot>(),
    configVersionId: text("config_version_id"), // Link to store configuration version
    configLocale: varchar("config_locale", { length: 10 }), // Locale for config overrides (e.g., 'nl', 'be-nl')
    status: projectStatusEnum().notNull().default("draft"), // Project lifecycle status
    tags: text("tags").array().notNull().default([]), // Free-text labels for filtering (e.g. set by bulk publish)
    internalNotes: text("internal_notes"), // Internal notes about the project
    approvedBy: text("approved_by"), // User ID who approved the project
    approvedAt: timestamp("approved_at", { withTimezone: true }), // When the project was approved
  },
  (table) =>
    [
      foreignKey({
        columns: [table.storeId],
        foreignColumns: [stores.id],
        name: "project_store_id_stores_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("set null"),
      foreignKey({
        columns: [table.configVersionId],
        foreignColumns: [configurationVersions.id],
        name: "project_config_version_id_configuration_versions_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("set null"),
      foreignKey({
        columns: [table.approvedBy],
        foreignColumns: [user.id],
        name: "project_approved_by_user_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("set null"),
      index("project_store_id_idx").on(table.storeId),
      index("project_config_version_id_idx").on(table.configVersionId),
      index("project_approved_by_idx").on(table.approvedBy),
      index("project_name_trgm_idx").using("gin", table.name.op("gin_trgm_ops")),
      index("project_location_trgm_idx").using("gin", table.location.op("gin_trgm_ops")),
      index("project_author_trgm_idx").using("gin", table.author.op("gin_trgm_ops")),
      index("project_name_idx").on(table.name),
      index("project_location_idx").on(table.location),
      index("project_author_idx").on(table.author),
      index("project_status_idx").on(table.status),
      index("project_tags_idx").using("gin", table.tags),
    ] as const,
)

export const projectLabels = pgTable(
  "project_labels",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    name: text().notNull(),
    slug: text().notNull(),
    description: text(),
    color: text(),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
  },
  (table) =>
    [
      foreignKey({
        columns: [table.createdBy],
        foreignColumns: [user.id],
        name: "project_labels_created_by_user_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("set null"),
      uniqueIndex("project_labels_slug_idx").on(table.slug),
      index("project_labels_name_idx").on(table.name),
      index("project_labels_created_by_idx").on(table.createdBy),
      index("project_labels_archived_at_idx").on(table.archivedAt),
    ] as const,
)

export const projectLabelAssignments = pgTable(
  "project_label_assignments",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    projectId: text("project_id").notNull(),
    labelId: text("label_id").notNull(),
    assignedBy: text("assigned_by"),
    assignedAt: timestamp("assigned_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) =>
    [
      foreignKey({
        columns: [table.projectId],
        foreignColumns: [projectsTable.id],
        name: "project_label_assignments_project_id_project_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("cascade"),
      foreignKey({
        columns: [table.labelId],
        foreignColumns: [projectLabels.id],
        name: "project_label_assignments_label_id_project_labels_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("cascade"),
      foreignKey({
        columns: [table.assignedBy],
        foreignColumns: [user.id],
        name: "project_label_assignments_assigned_by_user_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("set null"),
      uniqueIndex("project_label_assignments_project_label_idx").on(table.projectId, table.labelId),
      index("project_label_assignments_project_id_idx").on(table.projectId),
      index("project_label_assignments_label_id_idx").on(table.labelId),
      index("project_label_assignments_assigned_by_idx").on(table.assignedBy),
    ] as const,
)

// Project presence tracking - who is viewing/editing a project
export const projectPresence = pgTable(
  "project_presence",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    projectId: text("project_id").notNull(),
    userId: text("user_id").notNull(),
    lastPing: timestamp("last_ping", { withTimezone: true }).defaultNow().notNull(),
    isEditing: boolean("is_editing").default(false).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) =>
    [
      foreignKey({
        columns: [table.projectId],
        foreignColumns: [projectsTable.id],
        name: "project_presence_project_id_project_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("cascade"),
      foreignKey({
        columns: [table.userId],
        foreignColumns: [user.id],
        name: "project_presence_user_id_user_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("cascade"),
      index("project_presence_project_id_idx").on(table.projectId),
      index("project_presence_user_id_idx").on(table.userId),
      index("project_presence_last_ping_idx").on(table.lastPing),
      // Unique constraint: one presence record per user per project
      uniqueIndex("project_presence_project_user_idx").on(table.projectId, table.userId),
      // Partial unique: only ONE user per project can have is_editing=true
      uniqueIndex("project_presence_single_editor_idx")
        .on(table.projectId)
        .where(sql`is_editing = true`),
    ] as const,
)

export const fixtureValidationRuns = pgTable(
  "fixture_validation_runs",
  {
    id: serial().primaryKey().notNull(),
    projectId: text("project_id").notNull(),
    storeId: text("store_id"),
    projectName: text("project_name").notNull(),
    floorPlanReference: text("floor_plan_reference").notNull(),
    sourceFilename: text("source_filename"),
    triggerSource: fixtureValidationTriggerSourceEnum("trigger_source").notNull(),
    cropPassed: boolean("crop_passed").notNull(),
    status: fixtureValidationStatusEnum().notNull(),
    expectedCounts: jsonb("expected_counts")
      .$type<{ gondola: number | null; wall: number | null; endcap: number | null }>()
      .notNull(),
    detectedCounts: jsonb("detected_counts")
      .$type<{ gondola: number | null; wall: number | null; endcap: number | null }>()
      .notNull(),
    failureReason: text("failure_reason"),
    inputHash: text("input_hash").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) =>
    [
      foreignKey({
        columns: [table.projectId],
        foreignColumns: [projectsTable.id],
        name: "fixture_validation_runs_project_id_project_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("cascade"),
      foreignKey({
        columns: [table.storeId],
        foreignColumns: [stores.id],
        name: "fixture_validation_runs_store_id_stores_id_fk",
      })
        .onUpdate("cascade")
        .onDelete("set null"),
      index("fixture_validation_runs_project_id_idx").on(table.projectId),
      index("fixture_validation_runs_store_id_idx").on(table.storeId),
      index("fixture_validation_runs_status_idx").on(table.status),
      index("fixture_validation_runs_created_at_idx").on(table.createdAt),
    ] as const,
)

// Store configurations with temporal versioning
export const storeConfigurations = pgTable(
  "store_configurations",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    storeId: text("store_id").notNull(),
    name: text().notNull().default("Current layout"),
    // Temporal/mutable store data that can change over time
    sfa: integer(), // Sales Floor Area (m²)
    storeSize: text("store_size"), // free-form label resolved against `store_size_buckets`
    totalRacks: integer("total_racks"),
    gondolas: integer(),
    wallRacks: integer("wall_racks"),
    gondolaEnds: integer("gondola_ends"),
    /**
     * Manually-entered count of gondola rows in the store (BLU-492 col 19).
     * Per Action's "Winkel Layout Informatie" handleiding: "Total number
     * of gondolas" read off the layout. Distinct from `gondolas` which
     * is the meter-count for the "G" Store Info field.
     */
    gondolaRows: integer("gondola_rows"),
    /** @deprecated — not in Action's "Winkel Layout Informatie" handleiding;
     * never consumed beyond storage. DB column retained for legacy data. */
    promoAreas: integer("promo_areas"),
    offShelfDisplays: integer("off_shelf_displays"),
    displayFixtures: integer("display_fixtures"),
    /** @deprecated — not in Action's "Winkel Layout Informatie" handleiding;
     * never consumed beyond storage. DB column retained for legacy data. */
    bulkPallets: integer("bulk_pallets"),
    seasonPallets: integer("season_pallets"),
    promoPallets: integer("promo_pallets"),
    drinksPallets: integer("drinks_pallets"),
    /** @deprecated — replaced by `palletsKt`; never consumed beyond storage.
     * DB column retained for legacy data. */
    kitchenTableSection: integer("kitchen_table_section"),
    /** Kitchen + Toiletery pallets (BLU-492 col 26 — distinct from bulkPallets). */
    palletsKt: integer("pallets_kt"),
    batteriesSmall: integer("batteries_small"),
    batteriesLarge: integer("batteries_large"),
    batteriesSco: integer("batteries_sco"),
    /** Straight servicedesk batteries (fixture type `balie_batteries`, code SD). */
    batteriesServicedesk: integer("batteries_servicedesk"),
    impulsBaskets: integer("impuls_baskets"),
    promoBaskets: integer("promo_baskets"),
    /** Promo gondola ends — distinct from `gondolaEnds` (the total GE count). */
    promoGondolaEnds: integer("promo_gondola_ends"),
    /** Sustainability gondola ends — distinct from `gondolaEnds`. */
    sustainabilityGondolaEnds: integer("sustainability_gondola_ends"),
    checkouts: integer(),
    selfCheckouts: integer("self_checkouts"),
    /** Full Check Out — counter checkouts dedicated to specific service flows (BLU-492 col 30). */
    fco: integer(),
    /** Cash Check Out — cash-only checkouts (BLU-492 col 31). */
    cco: integer(),
    /** "Counter" — Action's column 32; semantics confirmed via BLU-492. */
    counterCount: integer("counter_count"),
    /** Number of customer baskets in the store (BLU-492 col 33). */
    baskets: integer(),
    /** Number of customer trolleys (BLU-492 col 34). */
    trolleys: integer(),
    /** Number of shopping carts (BLU-492 col 35). */
    shoppingCartsCount: integer("shopping_carts_count"),
    // Versioning fields
    validFrom: timestamp("valid_from", { withTimezone: true }).defaultNow().notNull(),
    validTo: timestamp("valid_to", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.storeId],
      foreignColumns: [stores.id],
      name: "store_configurations_store_id_stores_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("store_configurations_current_idx")
      .on(table.storeId, table.validFrom)
      .where(sql`${table.validTo} IS NULL`),
  ],
)

// Additional store metadata that doesn't change often
export const storeMetadataExtensions = pgTable(
  "store_metadata_extensions",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    storeId: text("store_id").notNull().unique(),
    // Configuration & layout fields
    direction: text(), // L | R
    /**
     * @deprecated Removed from the application surface (no form input, no
     * Query Builder filter, no report column) per Action May 2026 — High
     * Risk is now a value of `entrance_gate`, not a separate Y/N flag.
     * Column kept in the DB until a follow-up contract migration drops it
     * (expand-contract pattern). Do not re-introduce reads or writes.
     */
    highRisk: text("high_risk"),
    shelves1m25Count: integer("shelves_1m25_count"),
    angledFixtures: text("angled_fixtures"), // Y | N
    /** Blue buying-meter grids mounted along walls. */
    blueGridsWall: integer("blue_grids_wall"),
    /** Blue buying-meter grids on gondolas. */
    blueGridsGondola: integer("blue_grids_gondola"),
    /** Orange promo buying-meter grids. */
    orangeGridsPromo: integer("orange_grids_promo"),
    /** Green sustainability buying-meter grids. */
    greenGridsSustainability: integer("green_grids_sustainability"),
    /** Entrance gate shape — "Small" | "Wide" | "High Risk" (BLU-492 col 7, per Action May 2026). */
    entranceGate: text("entrance_gate"),
    /** Whether the store has an information screen — "Y" | "N" (BLU-492 col 8). */
    infoscreen: text(),
    /** Service-counter shape — "Straight" | "L-Shape" | "Back to back" | "None" (BLU-492 col 27). */
    serviceCounter: text("service_counter"),
    /** Where the customer-cart corral is — "Inside" | "Outside" | "Inside/Outside" | "None" (BLU-492 col 36). */
    shoppingCartsLocation: text("shopping_carts_location"),
    /** Free-text exception flags (BLU-492 col 11 — seen values: "ADR"). */
    exceptions: text(),
    // Blueprint information
    blueprintLastUpdate: text("blueprint_last_update"),
    blueprintVersion: text("blueprint_version"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    lastModified: timestamp("last_modified", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.storeId],
      foreignColumns: [stores.id],
      name: "store_metadata_extensions_store_id_stores_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
)

// =============================================================================
// User invitations (pre-configure access before first login)
// =============================================================================

/**
 * User invitations allow admins to pre-configure role and store access
 * for users before they log in for the first time via Azure AD.
 *
 * When a user logs in and their email matches a pending invitation,
 * the invitation settings are applied to their account.
 */
export const userInvitations = pgTable(
  "user_invitations",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    // Email to match on first login (unique - only one pending invite per email)
    email: text().notNull().unique(),
    // Pre-configured role (optional - Azure AD role takes precedence if set)
    role: text(),
    // Pre-configured store access scoping
    allowedCountryIds: text("allowed_country_ids"),
    allowedStoreIds: text("allowed_store_ids"),
    // Invitation metadata
    invitedById: text("invited_by_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    // Claimed when user logs in and invitation is applied
    claimedAt: timestamp("claimed_at", { withTimezone: true }),
    claimedByUserId: text("claimed_by_user_id"),
  },
  (table) => [
    foreignKey({
      columns: [table.invitedById],
      foreignColumns: [user.id],
      name: "user_invitations_invited_by_id_user_id_fk",
    }).onDelete("set null"),
    foreignKey({
      columns: [table.claimedByUserId],
      foreignColumns: [user.id],
      name: "user_invitations_claimed_by_user_id_fk",
    }).onDelete("set null"),
    index("user_invitations_email_idx").on(table.email),
  ],
)

// =============================================================================
// Store Files Management
// =============================================================================

/**
 * Central registry of all files associated with stores.
 * Tracks input files (blueprints), intermediate files (cropped images),
 * and output files (reports, converter exports) with versioning.
 */
export const files = pgTable(
  "files",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),

    // Type discriminator
    type: fileTypeEnum().notNull(),

    // Associations (storeId required, projectId optional for store-level files)
    storeId: text("store_id").notNull(),
    projectId: text("project_id"),

    // Metadata
    fileName: text("file_name").notNull(),
    version: text().notNull(), // ISO timestamp: "2025-01-15T10:30:45"
    userId: text("user_id").notNull(),
    note: text(), // Optional user-provided description

    // Storage
    storagePath: text("storage_path").notNull(),
    storageBackend: storageBackendEnum("storage_backend").notNull(),
    size: integer().notNull(), // bytes
    contentType: text("content_type").notNull(), // MIME type

    // Timestamps
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }), // Set when note is edited
    deletedAt: timestamp("deleted_at", { withTimezone: true }), // Soft delete timestamp
  },
  (table) => [
    foreignKey({
      columns: [table.storeId],
      foreignColumns: [stores.id],
      name: "files_store_id_stores_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.projectId],
      foreignColumns: [projectsTable.id],
      name: "files_project_id_project_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: "files_user_id_user_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    index("files_store_id_idx").on(table.storeId),
    index("files_project_id_idx").on(table.projectId),
    index("files_type_idx").on(table.type),
    index("files_created_at_idx").on(table.createdAt),
    index("files_user_id_idx").on(table.userId),
    // Composite index for common query patterns
    index("files_store_type_created_idx").on(table.storeId, table.type, table.createdAt),
  ],
)

export type FileType = (typeof fileTypeEnum.enumValues)[number]
export type StorageBackend = (typeof storageBackendEnum.enumValues)[number]

/**
 * File shares for external access via magic links.
 * Allows sharing specific files with external users without full account access.
 */
export const fileShares = pgTable(
  "file_shares",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),

    // The file being shared
    fileId: text("file_id").notNull(),

    // Share token for magic link
    token: text().notNull().unique(),

    // Optional note for the share (displayed to recipient)
    note: text(),

    // Share metadata
    createdById: text("created_by_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),

    // Access tracking
    accessedAt: timestamp("accessed_at", { withTimezone: true }),
    accessCount: integer("access_count").notNull().default(0),

    // Revocation
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
  },
  (table) => [
    foreignKey({
      columns: [table.fileId],
      foreignColumns: [files.id],
      name: "file_shares_file_id_files_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.createdById],
      foreignColumns: [user.id],
      name: "file_shares_created_by_id_user_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    index("file_shares_file_id_idx").on(table.fileId),
    index("file_shares_token_idx").on(table.token),
    index("file_shares_expires_at_idx").on(table.expiresAt),
  ],
)

// Bulk operation job status enum
export const bulkOperationStatusEnum = pgEnum("bulk_operation_status", [
  "pending",
  "running",
  "completed",
  "failed",
])

// Bulk operation type enum
export const bulkOperationTypeEnum = pgEnum("bulk_operation_type", [
  "project_export",
  "project_update_config",
  "project_archive",
  "project_reassign_store",
  "store_update_config",
  "store_update_metadata",
  "materials_export",
  "lom_fill_in_impact",
])

/**
 * Bulk operation jobs for tracking long-running operations.
 * Used for operations affecting 50+ items to provide progress feedback.
 */
export const bulkOperationJobs = pgTable(
  "bulk_operation_jobs",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),

    // Operation details
    type: bulkOperationTypeEnum().notNull(),
    status: bulkOperationStatusEnum().notNull().default("pending"),

    // Progress tracking
    totalCount: integer("total_count").notNull(),
    completedCount: integer("completed_count").notNull().default(0),
    failedCount: integer("failed_count").notNull().default(0),

    // Error tracking (JSONB array of error messages)
    errors: jsonb("errors").$type<Array<{ id: string; error: string }>>(),

    // Result data (e.g., download URL for exports)
    result: jsonb("result").$type<Record<string, unknown>>(),

    // Timestamps
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    startedAt: timestamp("started_at", { withTimezone: true }),
    completedAt: timestamp("completed_at", { withTimezone: true }),

    // User who initiated the operation
    userId: text("user_id").notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: "bulk_operation_jobs_user_id_user_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("bulk_operation_jobs_user_id_idx").on(table.userId),
    index("bulk_operation_jobs_status_idx").on(table.status),
    index("bulk_operation_jobs_created_at_idx").on(table.createdAt),
  ],
)

export type BulkOperationStatus = (typeof bulkOperationStatusEnum.enumValues)[number]
export type BulkOperationType = (typeof bulkOperationTypeEnum.enumValues)[number]

// =============================================================================
// Country Code Mappings
// =============================================================================

export const countryCodeMappings = pgTable(
  "country_code_mappings",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    // Store number range boundaries (lexicographic comparison for alphanumeric support).
    // endRange is optional: some regions are identified by a start range only.
    startRange: text("start_range").notNull(),
    endRange: text("end_range"),
    // Display code shown on PDF export (e.g. "NL", "BE-Vlaanderen", "CH-DE").
    // Unique: it is the natural key that stores.region_code points at.
    displayCode: text("display_code").notNull().unique(),
    // Country this region belongs to. Set explicitly on the mappings page so the
    // store creation selector and reports don't have to infer it from the
    // display code. Nullable for legacy rows until backfilled.
    countryId: text("country_id"),
    // Default store/config language for this region (e.g. "nl-BE" for BE-FL).
    // DB-driven so a new region gets a locale without a code change. Nullable:
    // a region without a locale just doesn't auto-fill one.
    locale: text("locale"),
    // Timestamps
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date()),
    // Who created it
    createdBy: text("created_by"),
  },
  (table) => [
    foreignKey({
      columns: [table.createdBy],
      foreignColumns: [user.id],
      name: "country_code_mappings_created_by_user_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.countryId],
      foreignColumns: [countries.id],
      name: "country_code_mappings_country_id_countries_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    index("country_code_mappings_range_idx").on(table.startRange, table.endRange),
  ],
)

// =============================================================================
// Store Size Buckets
// =============================================================================
//
// Maps a rack count to a canonical store-size label (XXS..XXL). The label set
// is intentionally fixed (the type system and report layouts depend on it);
// only the rack-count thresholds per bucket are editable from Settings.
//
// Defaults are seeded by migration 0055. `from_racks`/`to_racks` are inclusive
// bounds; `null` means "no lower bound" (XXS) or "no upper bound" (XXL). The
// active set must cover all 7 sizes contiguously without overlap; this is
// enforced server-side, not at the DB level (so a planner can save partial
// edits within a single transaction).

export const storeSizeBuckets = pgTable(
  "store_size_buckets",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    size: text().notNull(), // free-form label, e.g. "XXS"..."XXL" by default; planners can rename or extend
    fromRacks: integer("from_racks"),
    toRacks: integer("to_racks"),
    sortOrder: integer("sort_order").notNull(),
    lastModified: timestamp("last_modified", { withTimezone: true }).defaultNow().notNull(),
    lastModifiedBy: text("last_modified_by"),
  },
  (table) => [
    uniqueIndex("store_size_buckets_size_unique").on(table.size),
    foreignKey({
      columns: [table.lastModifiedBy],
      foreignColumns: [user.id],
      name: "store_size_buckets_last_modified_by_user_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
  ],
)

// =============================================================================
// Standard Store Baseline
// =============================================================================
//
// Action's "Standaard winkel" — the reference store used as the top
// Blueprint row in the Vergelijking met Inkoopmeters report (BLU-498)
// and as the "Standaard" row in Stores by Size (BLU-494). Used to be
// hardcoded in the reports feature; moved to DB so planners can update
// it without a code-change when Action revises the inkoopmeters
// baseline (~1× per 2 years). Seed-defaults live in
// `features/standard-store/core/defaultStandardStoreBaseline.ts`.
//
// Singleton — id is locked to a fixed value. JSON for meters-per-MG
// because MG codes are stable string identifiers (no FK needed; missing
// codes render as 0 in the report, preserving existing tolerance).
// Seeded with the historic hardcoded values in migration.

export const standardStoreBaseline = pgTable(
  "standard_store_baseline",
  {
    id: text().primaryKey().notNull().default("__current__"),
    gondolas: integer().notNull(),
    walls: integer().notNull(),
    racks: integer().notNull(),
    metersPerMg: jsonb("meters_per_mg").notNull(),
    lastModified: timestamp("last_modified", { withTimezone: true }).defaultNow().notNull(),
    lastModifiedBy: text("last_modified_by"),
  },
  (table) => [
    foreignKey({
      columns: [table.lastModifiedBy],
      foreignColumns: [user.id],
      name: "standard_store_baseline_last_modified_by_user_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
  ],
)

// =============================================================================
// Shelf Type Order
// =============================================================================
//
// Planner-controlled ordering for the overlay shelf-types (pallet group:
// promo_shelf, display_bulk, kitchen_toiletpaper_pallet, batteries,
// gondola ends, etc.). Used by reports (Store Info / BLU-492, Meters
// Overview, BOM) and by the Optimize-page context menu to lay these
// types out in the planner-defined order — used to be hardcoded in
// `shelfTypeRegistry.getOptimizeShelfTypeSections()`.
//
// One row per shelf-type. The set of valid `shelf_type` values is the
// `ShelveType` enum; we store them as `text` so the DB doesn't constrain
// it (the FE+BE enum already do that). Rows for types not in the active
// registry are silently ignored on read; types in the registry without a
// row use their registry-default order. Seeded by migration.
//
// Identity / visual properties (color, icon, code, label) stay in code —
// `shelfTypeRegistry.tsx` is still the single source of truth there.

export const shelfTypeOrder = pgTable(
  "shelf_type_order",
  {
    shelfType: text("shelf_type").primaryKey().notNull(),
    sortOrder: integer("sort_order").notNull(),
    isVisible: boolean("is_visible").notNull().default(true),
    lastModified: timestamp("last_modified", { withTimezone: true }).defaultNow().notNull(),
    lastModifiedBy: text("last_modified_by"),
  },
  (table) => [
    foreignKey({
      columns: [table.lastModifiedBy],
      foreignColumns: [user.id],
      name: "shelf_type_order_last_modified_by_user_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
  ],
)
